import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { ArrowLeft, BookOpen, Printer, CheckSquare, Square } from 'lucide-react';
import { toast } from 'sonner';
import PracticeScenario from '../shared/PracticeScenario';
import PageMessaging from '../shared/PageMessaging';

export default function CompassionateCommunicationChecklist() {
  const navigate = useNavigate();
  const printPage = () => { window.print(); toast.success('Print dialog opened'); };

  const checklistItems = [
    {
      category: 'Before the Conversation',
      items: [
        'I am clear about my intention for the conversation',
        'I have identified my feelings about the situation',
        'I have connected with my underlying needs',
        'I am open to hearing the other person\'s perspective',
        'I have chosen a good time and place for the conversation',
        'I am grounded and present'
      ]
    },
    {
      category: 'During the Conversation - Observations',
      items: [
        'I described what I observed without evaluation or judgment',
        'I used specific, concrete language',
        'I avoided words like "always," "never," "should"',
        'I stayed focused on facts rather than interpretations'
      ]
    },
    {
      category: 'During the Conversation - Feelings',
      items: [
        'I expressed my genuine feelings',
        'I took responsibility for my feelings',
        'I avoided "pseudo-feelings" (e.g., "I feel that you...")',
        'I stayed connected to my body sensations'
      ]
    },
    {
      category: 'During the Conversation - Needs',
      items: [
        'I identified and expressed my universal needs',
        'I distinguished needs from strategies',
        'I acknowledged the other person\'s needs',
        'I looked for ways to meet both our needs'
      ]
    },
    {
      category: 'During the Conversation - Requests',
      items: [
        'I made clear, doable requests',
        'I asked for what I wanted (not what I didn\'t want)',
        'I checked if my request was understood',
        'I was open to hearing "no" and exploring alternatives'
      ]
    },
    {
      category: 'During the Conversation - Empathy',
      items: [
        'I listened with full presence',
        'I reflected back what I heard',
        'I guessed at the other person\'s feelings and needs',
        'I stayed curious rather than defensive',
        'I avoided giving advice, fixing, or explaining'
      ]
    },
    {
      category: 'After the Conversation',
      items: [
        'I appreciated myself for practicing NVC',
        'I noted what worked well',
        'I identified what I might do differently next time',
        'I followed through on any agreements made'
      ]
    }
  ];

  const [checked, setChecked] = React.useState({});

  const toggleCheck = (category, index) => {
    const key = `${category}-${index}`;
    setChecked(prev => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <style>{`@media print { body { background: white !important; } .no-print { display: none !important; } .print-page { background: white !important; color: black !important; } }`}</style>
      
      <div className="border-b border-slate-700/50 bg-slate-900/90 backdrop-blur-sm sticky top-0 z-50 no-print">
        <div className="container mx-auto px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button onClick={() => navigate('/belonging-ebook')} variant="outline" size="sm" className="border-slate-600 hover:border-slate-500 text-white hover:bg-slate-800">
                <ArrowLeft className="h-4 w-4 mr-2" />Back to Workbook
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-white">Compassionate Communication Checklist</h1>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button onClick={printPage} variant="outline" size="sm" className="border-blue-600 hover:bg-blue-900/20 text-white">
                <Printer className="h-4 w-4 mr-2" />Print
              </Button>
              <div className="flex items-center gap-2 text-slate-400 text-sm">
                <BookOpen className="h-4 w-4" />
                <span>Page 37</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 py-12 print-page">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-white mb-4">Compassionate Communication Checklist</h2>
            <p className="text-slate-300 max-w-2xl mx-auto">Use this checklist to prepare for and reflect on important conversations. Click items to mark them as complete.</p>
          </div>

          <div className="space-y-8">
            {checklistItems.map((section, sectionIndex) => (
              <div key={sectionIndex} className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-6">
                <h3 className="text-xl font-bold text-amber-400 mb-4">{section.category}</h3>
                <div className="space-y-3">
                  {section.items.map((item, itemIndex) => {
                    const key = `${section.category}-${itemIndex}`;
                    const isChecked = checked[key];
                    return (
                      <button
                        key={itemIndex}
                        onClick={() => toggleCheck(section.category, itemIndex)}
                        className={`w-full flex items-start gap-3 p-3 rounded-lg transition-all text-left ${isChecked ? 'bg-green-900/30 border-green-700/50' : 'bg-slate-700/30 hover:bg-slate-700/50'} border border-slate-600/50`}
                      >
                        {isChecked ? (
                          <CheckSquare className="h-5 w-5 text-green-400 flex-shrink-0 mt-0.5" />
                        ) : (
                          <Square className="h-5 w-5 text-slate-400 flex-shrink-0 mt-0.5" />
                        )}
                        <span className={`${isChecked ? 'text-green-200 line-through' : 'text-slate-200'}`}>{item}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-10 bg-blue-900/20 border border-blue-700/30 rounded-lg p-6">
            <h3 className="text-lg font-bold text-blue-200 mb-3">💡 Practice Tip</h3>
            <p className="text-blue-100">Print this checklist and keep it handy for important conversations. With practice, these skills will become second nature.</p>
          </div>

          <PracticeScenario pageTitle="Compassionate Communication Checklist" pageNumber={37} />
          <PageMessaging pageTitle="Compassionate Communication Checklist" pageNumber={37} />
        </div>
      </div>
    </div>
  );
}
